import React, { Fragment, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';
import type { MessageDescriptor } from 'react-intl-next';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { SpaceSelector } from '@confluence/space-selector';

import { SelectLabel, selectStyles } from './LabeledSelect.tsx';

export const i18n = defineMessages({
	sourceSpaceValidationError: {
		id: 'copy-space-permissions.option-select.source-space.validation-error',
		defaultMessage: 'This space can’t be used as a source.',
		description:
			'The validation message appearing with an invalid source space selection, for users selecting a permission set to copy in Copy Space Permissions UI',
	},
	sourceSpaceGuestsWarning: {
		id: 'copy-space-permissions.option-select.source-space.guests-warning',
		defaultMessage:
			'The guests in this space won’t be included because guests can only have access to one space at a time.',
		description: 'The text that warns that guest permissions are not copied from the source space.',
	},
});

export const EMPTY_SPACE = {
	isRestricted: false,
	containsExternalCollaborators: false,
	spaceKey: '',
	spaceName: '',
	isAdmin: false,
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ValidationMessage = styled.div<{
	isExtraTall: boolean;
	isErrorMessage?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props) => (props.isErrorMessage ? token('color.text.danger') : 'unset'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minHeight: (props) => (props.isExtraTall ? '72px' : '20px'),
});

export type SpaceOptionType = {
	isRestricted: boolean;
	containsExternalCollaborators: boolean;
	spaceKey: string;
	spaceName: string;
	isAdmin: boolean;
	id: string;
};
export type SourceSpaceSelectorProps = {
	label?: MessageDescriptor;
	onChange: (selection: SpaceOptionType) => void;
	placeholder?: MessageDescriptor;
	validationState?: 'default' | 'error' | 'success';
	filterSourceSpaceOptions?: (option: SpaceOptionType) => boolean;
	renderGuestsWarning?: boolean;
	fullWidth?: boolean;
	validationPlaceholder?: boolean;
	defaultSpaceKey?: string;
	allowOnlyAdminSpaces?: boolean;
	showSpaceLabel?: boolean;
	labelTextTransform?: string;
};

export const SourceSpaceSelector = ({
	label,
	onChange,
	filterSourceSpaceOptions,
	placeholder,
	validationState = 'default',
	renderGuestsWarning = false,
	fullWidth = false,
	validationPlaceholder = false,
	defaultSpaceKey,
	allowOnlyAdminSpaces,
	showSpaceLabel = false,
	labelTextTransform,
}: SourceSpaceSelectorProps) => {
	const [selection, setSelection] = useState(EMPTY_SPACE);
	const onChangeInternal = (newSelection: SpaceOptionType) => {
		setSelection(newSelection);
		onChange(newSelection);
	};

	const errorMessage = validationState === 'error' ? i18n.sourceSpaceValidationError : null;
	const infoMessage =
		renderGuestsWarning && selection.containsExternalCollaborators
			? i18n.sourceSpaceGuestsWarning
			: null;

	return (
		<Fragment>
			{label && (
				<SelectLabel htmlFor="copy-space-perms-space-select">
					<FormattedMessage {...label} />
				</SelectLabel>
			)}
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */}
			<div style={selectStyles(fullWidth)}>
				<SpaceSelector
					id="copy-space-perms-space-select"
					onChange={onChangeInternal}
					placeholder={placeholder && <FormattedMessage {...placeholder} />}
					showDefaultSpace={false}
					showSpaceLabel={showSpaceLabel}
					validationState={validationState}
					filterOption={filterSourceSpaceOptions}
					spaceKey={defaultSpaceKey}
					allowOnlyAdminSpaces={allowOnlyAdminSpaces}
					lazyLoadOptions
					labelTextTransform={labelTextTransform}
				/>
			</div>

			{/* Always render the validation section if validationPlaceholder
      is set even if there is no message to display, maintaining a fixed
      height, so that when we DO show a message it doesn't cause other
      content to jump around.

      If we have a label to display in front of the space selector, e.g.
      in create new space, it will align the selector to the right,
      beside the label. Ensure our validation message below is also
      properly aligned to the right, beneath the source space selector,
      by rendering an empty SelectLabel first. */}
			{label && <SelectLabel />}
			{(validationPlaceholder || errorMessage || infoMessage) && (
				<ValidationMessage
					id="copy-space-perms-space-select-validation-message"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					style={selectStyles(fullWidth)}
					isExtraTall={validationPlaceholder && renderGuestsWarning}
					isErrorMessage={!!errorMessage}
				>
					{errorMessage && <FormattedMessage {...errorMessage} />}
					{!errorMessage && infoMessage && <FormattedMessage {...infoMessage} />}
				</ValidationMessage>
			)}
		</Fragment>
	);
};
